import { Plugin } from './Plugin';
import { WebGLCubeRenderTarget, MeshStandardMaterial, RGBAFormat, LinearMipmapLinearFilter, sRGBEncoding, CubeCamera, Vector3, Box3, CubeRefractionMapping } from 'three';
import { EntityPlugin } from './EntityPlugin';
import { ScenePlugin } from './ScenePlugin';
import { GlobalsPlugin } from './GlobalsPlugin';
import { LightingPlugin } from './LightingPlugin';
import { SpotLightShadowsPlugin } from './SpotLightShadowsPlugin';
import { TextureLoader } from '../loaders/TextureLoader';
import { APP_URL } from 'js/common/AppUrl';

import { debugCubeRenderTarget } from '../helpers/DebugTexture';

export class DynamicEnvironmentMapPlugin extends Plugin{
	constructor(manager){
		super(manager);

		this.dependencies.add(ScenePlugin);
		this.dependencies.add(EntityPlugin);
		this.dependencies.add(GlobalsPlugin);
		this.dependencies.add(LightingPlugin);
		this.dependencies.add(SpotLightShadowsPlugin);

		this.count = 0;

		this.sceneLoaded = false;

		this.cubeRenderTarget1 = new WebGLCubeRenderTarget( 512, {
			format: RGBAFormat,
			generateMipmaps: true,
			minFilter: LinearMipmapLinearFilter,
			encoding: sRGBEncoding // temporary -- to prevent the material's shader from recompiling every frame
		} );
		this.cubeRenderTarget1.texture._needsFlipEnvMap = false;

		this.cubeCamera1 = new CubeCamera( 0.01, 1000, this.cubeRenderTarget1 );

		this.cubeRenderTarget2 = new WebGLCubeRenderTarget( 512, {
			format: RGBAFormat,
			generateMipmaps: true,
			minFilter: LinearMipmapLinearFilter,
			encoding: sRGBEncoding
		} );
		this.cubeRenderTarget2.texture._needsFlipEnvMap = false;

		this.cubeCamera2 = new CubeCamera( 0.01, 1000, this.cubeRenderTarget2 );

		this.cubeCamera1.rotation.y = Math.PI;
		this.cubeCamera2.rotation.y = Math.PI;
		// this.cubeCamera1.scale.x = -1;
		// this.cubeCamera2.scale.x = -1;
		// this.cubeCamera1.scale.y = 1;
		// this.cubeCamera2.scale.y = 1;


		this.dynamicObjects = new Set();

		this._camera = this.cubeCamera1;

		// this.renderTargetDebug = debugCubeRenderTarget(this.viewer.rendering.renderer, 1536, 1000, 0, 0);

		this.envMapUrl = `${APP_URL}/assets/webgl/hdr-envmap.hdr`;

		this.textureLoader	= new TextureLoader();
		this.textureLoader.add(this.envMapUrl);

		this.enabled = false;
		window["DynamicEnvironmentMapPlugin"] = this;
	}

	get camera(){
		return this._camera;
	}

	get renderTarget(){
		return this.camera.renderTarget;
	}

	get texture(){
		return this.renderTarget.texture;
	}

	get ambientLight(){
		const plugin = this.manager.get(LightingPlugin);
		if(plugin.lights.size){
			return plugin.lights.get('ambient');
		}
		return null;
	}

	get entityPlugin(){
		return this.manager.get(EntityPlugin);
	}

	get scenePlugin(){
		return this.manager.get(ScenePlugin);
	}

	async attach(){
		this.entityPlugin.on('entity-added', this.onEntityUpdated);
		this.entityPlugin.on('entity-updated', this.onEntityUpdated);
		this.entityPlugin.on('entity-removed', this.onEntityRemoved);
		this.scenePlugin.on('scene-changed', this.onSceneUpdate);
		this.scenePlugin.on('scene-updated', this.onSceneUpdate);

		this.viewer.gltfLoader.on('progress', this.onProgress)

		// this.viewer.viewport.on('resize', this.renderTargetDebug.resize);
		await this.textureLoader.load();
	}

	detach(){
		this.entityPlugin.off('entity-added', this.onEntityUpdated);
		this.entityPlugin.off('entity-updated', this.onEntityUpdated);
		this.entityPlugin.off('entity-removed', this.onEntityRemoved);
		this.scenePlugin.off('scene-changed', this.onSceneUpdate);
		this.scenePlugin.off('scene-updated', this.onSceneUpdate);

		this.viewer.gltfLoader.off('progress', this.onProgress)

		// this.viewer.viewport.off('resize', this.renderTargetDebug.resize);
	}

	onSceneUpdate = (scene) => {
		this.sceneLoaded = true;
    	const center = new Vector3();
       	new Box3().setFromObject(scene).getCenter(center);

       	center.y = 1.75;
		this.cubeCamera1.position.copy(center);
		this.cubeCamera2.position.copy(center);

		this.render();
	}

	onEntityUpdated = (entity) => {

		const setupDynamicMaps = (e) => {
			entity.removeEventListener('loaded', setupDynamicMaps);
			this.render();
		}
		if(!entity.loaded){
			entity.addEventListener('loaded', setupDynamicMaps);
		}
		else setupDynamicMaps();
	}

	onEntityRemoved = (entity) => {
		this.render();
	}

	onProgress = (p) => {
		this.enabled = p == 1;

		this.render();

		if(this.enabled){
			this.viewer.gltfLoader.off('progress', this.onProgress)
		}
	}

	render = () => {
		if(!this.enabled) return;

		if(!this.entityPlugin.loaded) return;


		this._camera = this.count % 2 === 0 ? this.cubeCamera1 : this.cubeCamera2;

		//Force no light
		this.manager.get(LightingPlugin).visible = false;
		this.manager.get(SpotLightShadowsPlugin).light.visible = false;

		//Set default envmap
		this.manager.get(GlobalsPlugin).envMap =  this.textureLoader.get(this.envMapUrl);

		if(this.sceneLoaded){
			//Update cube camera textures
			this._camera.update(this.viewer.rendering.renderer, this.viewer.rendering.scene );

			//Set cube camera texture as envmap
			this.manager.get(GlobalsPlugin).envMap = this.renderTarget.texture;

		}

		//Set back light params
		this.manager.get(LightingPlugin).visible = true;
		this.manager.get(SpotLightShadowsPlugin).light.visible = true;

		this.count++;

		this.emit('change');
	}

	debug = () => {
		this.renderTargetDebug.render(this.renderTarget)
	}

}

				